import * as THREE from 'three';
import type { FVector as IFVector } from '@visualizer/types';

export default class FVector implements IFVector {
    x: number;
    y: number;
    z: number;

    constructor(x: number = 0, y: number = 0, z: number = 0) {
        this.x = x;
        this.y = y;
        this.z = z;
    }

    static safeCastToFloat(obj: unknown): number {
        if (typeof obj === 'number') {
            return obj;
        } else {
            throw new Error('Object is not a number: ' + String(obj));
        }
    }

    static add(v1: FVector, v2: FVector, target?: FVector): FVector {
        if (!target) {
            return new FVector(v1.x + v2.x, v1.y + v2.y, v1.z + v2.z);
        } else {
            target.set(v1.x + v2.x, v1.y + v2.y, v1.z + v2.z);
            return target;
        }
    }

    static sub(v1: FVector, v2: FVector, target?: FVector): FVector {
        if (!target) {
            return new FVector(v1.x - v2.x, v1.y - v2.y, v1.z - v2.z);
        } else {
            target.set(v1.x - v2.x, v1.y - v2.y, v1.z - v2.z);
            return target;
        }
    }

    static mult(v: FVector, n: number, target?: FVector): FVector {
        if (!target) {
            return new FVector(v.x * n, v.y * n, v.z * n);
        } else {
            target.set(v.x * n, v.y * n, v.z * n);
            return target;
        }
    }

    static div(v: FVector, n: number, target?: FVector): FVector {
        if (!target) {
            return new FVector(v.x / n, v.y / n, v.z / n);
        } else {
            target.set(v.x / n, v.y / n, v.z / n);
            return target;
        }
    }

    static dot(v: FVector, n: FVector): number {
        return n.x * v.x + n.y * v.y + n.z * v.z;
    }

    static cross(n: FVector, v: FVector): FVector {
        const crossX = n.y * v.z - v.y * n.z;
        const crossY = n.z * v.x - v.z * n.x;
        const crossZ = n.x * v.y - v.x * n.y;

        return new FVector(crossX, crossY, crossZ);
    }

    clone(): FVector {
        return new FVector(this.x, this.y, this.z);
    }

    static dist(n: FVector, v: FVector): number {
        const dx = n.x - v.x;
        const dy = n.y - v.y;
        const dz = n.z - v.z;
        return Math.sqrt(dx * dx + dy * dy + dz * dz);
    }

    static fromThree(vector: THREE.Vector3) {
        const x = vector.x;
        const y = vector.y;
        const z = vector.z;
        return new FVector(x, y, z);
    }
    mag(): number {
        return Math.sqrt(this.x * this.x + this.y * this.y + this.z * this.z);
    }

    magSq(): number {
        return this.x * this.x + this.y * this.y + this.z * this.z;
    }

    set(x: number, y: number, z: number): this {
        this.x = x;
        this.y = y;
        this.z = z;
        return this;
    }

    add(v: FVector): this {
        this.x += v.x;
        this.y += v.y;
        this.z += v.z;
        return this;
    }

    addScalar(n: number): this {
        this.x += n;
        this.y += n;
        this.z += n;
        return this;
    }

    sub(v: FVector): this {
        this.x -= v.x;
        this.y -= v.y;
        this.z -= v.z;
        return this;
    }

    mult(n: number): this {
        this.x *= n;
        this.y *= n;
        this.z *= n;
        return this;
    }

    div(n: number): this {
        this.x /= n;
        this.y /= n;
        this.z /= n;
        return this;
    }

    dist(v: FVector): number {
        const dx = this.x - v.x;
        const dy = this.y - v.y;
        const dz = this.z - v.z;
        return Math.sqrt(dx * dx + dy * dy + dz * dz);
    }

    dot(v: FVector): number {
        return this.x * v.x + this.y * v.y + this.z * v.z;
    }

    cross(v: FVector, target?: FVector): FVector {
        const crossX = this.y * v.z - v.y * this.z;
        const crossY = this.z * v.x - v.z * this.x;
        const crossZ = this.x * v.y - v.x * this.y;

        if (!target) {
            return new FVector(crossX, crossY, crossZ);
        } else {
            target.set(crossX, crossY, crossZ);
            return target;
        }
    }

    normalize(): this {
        const m = this.mag();
        if (m !== 0 && m !== 1) {
            this.div(m);
        }
        return this;
    }

    limit(max: number): this {
        if (this.magSq() > max * max) {
            this.normalize();
            this.mult(max);
        }
        return this;
    }

    setMag(len: number): this {
        this.normalize();
        this.mult(len);
        return this;
    }

    heading(): number {
        return Math.atan2(this.y, this.x);
    }

    rotate(theta: number): this {
        const temp = this.x;
        this.x = this.x * Math.cos(theta) - this.y * Math.sin(theta);
        this.y = temp * Math.sin(theta) + this.y * Math.cos(theta);
        return this;
    }

    static random3D(target?: FVector): FVector {
        const angle = Math.random() * Math.PI * 2;
        const vz = Math.random() * 2 - 1;
        const vx = Math.sqrt(1 - vz * vz) * Math.cos(angle);
        const vy = Math.sqrt(1 - vz * vz) * Math.sin(angle);

        if (!target) {
            return new FVector(vx, vy, vz);
        } else {
            target.set(vx, vy, vz);
            return target;
        }
    }

    angleBetween(v: FVector): number {
        if (this.x === 0 && this.y === 0 && this.z === 0) {
            return 0.0;
        }
        if (v.x === 0 && v.y === 0 && v.z === 0) {
            return 0.0;
        }

        const dot = this.x * v.x + this.y * v.y + this.z * v.z;
        const v1mag = Math.sqrt(this.x * this.x + this.y * this.y + this.z * this.z);
        const v2mag = Math.sqrt(v.x * v.x + v.y * v.y + v.z * v.z);
        const amt = dot / (v1mag * v2mag);

        if (amt <= -1) {
            return Math.PI;
        } else if (amt >= 1) {
            return 0;
        }

        return Math.acos(amt);
    }

    toArray(): [number, number, number] {
        return [this.x, this.y, this.z];
    }

    toString(): string {
        return `[${this.x}, ${this.y}, ${this.z}]`;
    }

    equals(obj: unknown): boolean {
        if (!(obj instanceof FVector)) {
            return false;
        }
        const p = obj as FVector;
        return this.x === p.x && this.y === p.y && this.z === p.z;
    }

    module(): number {
        return this.mag();
    }

    copy(v: FVector): this {
        this.x = v.x;
        this.y = v.y;
        this.z = v.z;
        return this;
    }

    distanceTo(vector: FVector): number {
        const dx = this.x - vector.x;
        const dy = this.y - vector.y;
        const dz = this.z - vector.z;
        return Math.sqrt(dx * dx + dy * dy + dz * dz);
    }

    multiplyScalar(scalar: number): this {
        return this.mult(scalar);
    }

    negate(): this {
        this.x = -this.x;
        this.y = -this.y;
        this.z = -this.z;
        return this;
    }

    static isZero(vector: { x: number; y: number; z: number }): boolean {
        return vector.x === 0 && vector.y === 0 && vector.z === 0;
    }
}
